import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AxiosError } from 'axios';
import { AdminCaseTableItem } from 'src/app/components/dashboard/cases/cases.component';
import {
  AddableFee,
  FeePayeeCase,
  FeePayeeCaseService,
} from 'src/app/services/fee-payee-case/fee-payee-case.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import { PayeeCasesService } from 'src/app/shared/payee-cases/payee-cases.service';
import { errorResponse } from 'src/types/error-response';
import { PayeeButtonComponent } from '../../shared/payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../shared/payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeDropdownComponent } from '../../shared/payee-form-fields/payee-dropdown/payee-dropdown.component';
import { PayeeFormFieldComponent } from '../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeIconComponent } from '../../shared/payee-icon/payee-icon.component';
import { PayeePopupComponent } from '../../shared/payee-popup/payee-popup.component';

@Component({
  selector: 'app-edit-fees-dialog',
  templateUrl: './edit-fees-dialog.component.html',
  styleUrls: ['./edit-fees-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    PayeeDropdownComponent,
    MatProgressSpinner,
    PayeeButtonComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    DecimalPipe,
  ],
})
export class EditFeesDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<EditFeesDialogComponent>>(MatDialogRef);
  private feePayeeCaseService = inject(FeePayeeCaseService);
  private payeeCasesService = inject(PayeeCasesService);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private snackbar = inject(MatSnackBar);

  data = inject<AdminCaseTableItem>(MAT_DIALOG_DATA);

  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly fees = signal<FeePayeeCase[]>([]);
  readonly addableFees = signal<AddableFee[]>([]);
  readonly selectedFee = signal<FeePayeeCase | null>(null);
  readonly initialPaidAmount = signal(0);
  readonly initialEventNetPaidAmount = signal(0);
  readonly savedOriginalAmount = signal(0);
  readonly savedPaidAmount = signal(0);

  readonly form = this.fb.group({
    original_amount: this.fb.control(0, {
      validators: [Validators.required, Validators.min(0)],
      nonNullable: true,
    }),
    paid_amount: this.fb.control(0, {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  readonly addFeeForm = this.fb.group({
    fee_id: this.fb.control<number | null>(null, Validators.required),
  });

  get originalAmount() {
    return this.form.controls.original_amount;
  }

  get paidAmountControl() {
    return this.form.controls.paid_amount;
  }

  get addFeeId() {
    return this.addFeeForm.controls.fee_id;
  }

  async ngOnInit(): Promise<void> {
    await this.loadFees();
  }

  readonly feeStateLabel = (state: FeePayeeCase['state']): string => {
    switch (state) {
      case 'pending':
        return 'Függőben';
      case 'paid':
        return 'Befizetve';
      case 'refunded':
        return 'Visszatérítve';
    }
  };

  readonly canDelete = (fee: FeePayeeCase): boolean =>
    fee.state === 'pending' && fee.paid_amount === 0;

  readonly remainingAmount = (): number => {
    const { original_amount, paid_amount } = this.form.getRawValue();
    return original_amount - paid_amount;
  };

  readonly canOverpay = (): boolean => {
    const { original_amount, paid_amount } = this.form.getRawValue();
    return paid_amount > original_amount;
  };

  readonly canRefund = (fee: FeePayeeCase): boolean =>
    fee.paid_amount > 0 && this.inferredRefundAmount() > 0;

  readonly inferredOverpaymentAmount = (): number => {
    const { original_amount, paid_amount } = this.form.getRawValue();
    return Math.max(0, paid_amount - original_amount);
  };

  readonly inferredRefundAmount = (): number =>
    Math.max(0, this.initialPaidAmount() - this.form.controls.paid_amount.value);

  readonly inferredPaymentAmount = (): number =>
    Math.max(0, this.form.controls.paid_amount.value - this.savedPaidAmount());

  readonly canRecordPayment = (): boolean => {
    const { original_amount, paid_amount } = this.form.getRawValue();

    return (
      this.form.valid
      && original_amount === this.savedOriginalAmount()
      && paid_amount > this.savedPaidAmount()
      && paid_amount <= original_amount
    );
  };

  readonly isRecordingFullPayment = (): boolean => {
    const { original_amount, paid_amount } = this.form.getRawValue();

    return paid_amount === original_amount;
  };

  readonly hasPendingAmountChanges = (): boolean => {
    const { original_amount, paid_amount } = this.form.getRawValue();

    return (
      original_amount !== this.savedOriginalAmount()
      || paid_amount !== this.savedPaidAmount()
    );
  };

  readonly canOverwrite = (): boolean => {
    const fee = this.selectedFee();

    return (
      this.hasPendingAmountChanges()
      && this.form.valid
      && !this.canRecordPayment()
      && !this.canOverpay()
      && !(fee !== null && this.canRefund(fee))
    );
  };

  readonly paidAmountDiffersFromEvents = (): boolean =>
    this.savedPaidAmount() !== this.initialEventNetPaidAmount();

  readonly addableFeeOptions = (): number[] =>
    this.addableFees().map((fee) => fee.fee_id);

  readonly addableFeeDisplayWith = (feeId: number | null): string => {
    if (feeId === null) {
      return '';
    }

    const fee = this.addableFees().find((item) => item.fee_id === feeId);
    if (!fee) {
      return '';
    }

    if (fee.type === 'unknown') {
      return fee.label;
    }

    return `${fee.label} (${fee.amount.toLocaleString('hu-HU')} ${fee.currency_iso})`;
  };

  selectFee(fee: FeePayeeCase): void {
    this.selectedFee.set(fee);
    this.initialPaidAmount.set(fee.paid_amount);
    this.initialEventNetPaidAmount.set(fee.event_net_paid_amount);
    this.savedOriginalAmount.set(fee.original_amount);
    this.savedPaidAmount.set(fee.paid_amount);
    this.form.patchValue({
      original_amount: fee.original_amount,
      paid_amount: fee.paid_amount,
    });
    this.form.markAsPristine();
  }

  copyOriginalToPaid(): void {
    const { original_amount, paid_amount } = this.form.getRawValue();
    if (paid_amount === original_amount) {
      return;
    }

    this.form.controls.paid_amount.setValue(original_amount);
  }

  copyPaidToOriginal(): void {
    const { original_amount, paid_amount } = this.form.getRawValue();
    if (paid_amount === original_amount) {
      return;
    }

    this.form.controls.original_amount.setValue(paid_amount);
  }

  async addSelectedFee(): Promise<void> {
    if (this.submitting() || this.addFeeForm.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.addFeeForm);
      return;
    }

    const feeId = this.addFeeForm.controls.fee_id.value;
    if (feeId === null) {
      return;
    }

    try {
      this.submitting.set(true);
      const fee = await this.feePayeeCaseService.addFee(this.data.id, feeId);
      await this.loadFees();
      this.selectFee(
        this.fees().find((item) => item.uuid === fee.uuid) ?? fee,
      );
      this.addFeeForm.reset();
      this.payeeCasesService.setCasesSubject.next();
    } catch (error) {
      this.handleError(error, this.addFeeForm);
    } finally {
      this.submitting.set(false);
    }
  }

  async deleteSelectedFee(): Promise<void> {
    const fee = this.selectedFee();
    if (!fee || !this.canDelete(fee) || this.submitting()) {
      return;
    }

    try {
      this.submitting.set(true);
      await this.feePayeeCaseService.deleteFee(this.data.id, fee.uuid);
      this.selectedFee.set(null);
      await this.loadFees();
      this.payeeCasesService.setCasesSubject.next();
    } catch (error) {
      this.handleError(error, this.form);
    } finally {
      this.submitting.set(false);
    }
  }

  async refundSelectedFee(): Promise<void> {
    const fee = this.selectedFee();
    const refundAmount = this.inferredRefundAmount();
    if (!fee || !this.canRefund(fee) || this.submitting()) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    try {
      this.submitting.set(true);
      const updated = await this.feePayeeCaseService.refundFee(
        this.data.id,
        fee.uuid,
        refundAmount,
      );
      await this.loadFees();
      this.selectFee(
        this.fees().find((item) => item.uuid === updated.uuid) ?? updated,
      );
      this.payeeCasesService.setCasesSubject.next();
    } catch (error) {
      this.handleError(error, this.form);
    } finally {
      this.submitting.set(false);
    }
  }

  async overwriteSelectedFee(): Promise<void> {
    const fee = this.selectedFee();
    if (!fee || !this.canOverwrite() || this.submitting()) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { original_amount, paid_amount } = this.form.getRawValue();

    try {
      this.submitting.set(true);
      const updated = await this.feePayeeCaseService.updateFee(
        this.data.id,
        fee.uuid,
        { original_amount, paid_amount, overwrite: true },
      );
      await this.loadFees();
      this.selectFee(
        this.fees().find((item) => item.uuid === updated.uuid) ?? updated,
      );
      this.payeeCasesService.setCasesSubject.next();
    } catch (error) {
      this.handleError(error, this.form);
    } finally {
      this.submitting.set(false);
    }
  }

  async recordSelectedFeePayment(): Promise<void> {
    if (!this.canRecordPayment() || this.submitting()) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    await this.saveAmounts({ recordPayment: true });
  }

  async saveOverpayment(): Promise<void> {
    const fee = this.selectedFee();
    if (!fee || !this.canOverpay() || this.submitting()) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    await this.saveAmounts({ allowPaidIncrease: true });
  }

  async saveAmounts(
    options: { allowPaidIncrease?: boolean; recordPayment?: boolean } = {},
  ): Promise<void> {
    const fee = this.selectedFee();
    if (!fee || this.submitting() || this.loading()) {
      return;
    }

    if (this.form.invalid) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const { original_amount, paid_amount } = this.form.getRawValue();

    if (original_amount !== this.savedOriginalAmount()) {
      return;
    }

    if (paid_amount > this.savedPaidAmount()) {
      if (paid_amount > original_amount && !options.allowPaidIncrease) {
        return;
      }

      if (paid_amount <= original_amount && !options.recordPayment) {
        return;
      }
    }

    if (paid_amount < this.savedPaidAmount()) {
      return;
    }

    if (!this.hasPendingAmountChanges()) {
      return;
    }

    try {
      this.submitting.set(true);
      const updated = await this.feePayeeCaseService.updateFee(
        this.data.id,
        fee.uuid,
        { original_amount, paid_amount },
      );
      await this.loadFees();
      this.selectFee(
        this.fees().find((item) => item.uuid === updated.uuid) ?? updated,
      );
      this.payeeCasesService.setCasesSubject.next();
    } catch (error) {
      this.handleError(error, this.form);
    } finally {
      this.submitting.set(false);
    }
  }

  readonly cancel = (): void => {
    if (this.submitting()) {
      return;
    }

    this.dialogRef.close(false);
  };

  private async loadFees(): Promise<void> {
    try {
      this.loading.set(true);
      const result = await this.feePayeeCaseService.getFees(this.data.id);
      this.fees.set(result.fees);
      this.addableFees.set(result.addable_fees);

      const selected = this.selectedFee();
      if (selected) {
        const refreshed = result.fees.find((fee) => fee.uuid === selected.uuid);
        if (refreshed) {
          this.selectFee(refreshed);
        } else {
          this.selectedFee.set(null);
        }
      } else if (result.fees.length > 0) {
        this.selectFee(result.fees[0]);
      }
    } catch (error: AxiosError | unknown) {
      if (error instanceof AxiosError) {
        console.error('Network error', error.response?.data);
      } else {
        console.error('Unknown error', error);
      }
      this.cancel();
    } finally {
      this.loading.set(false);
    }
  }

  private handleError(error: AxiosError | unknown, form: FormGroup): void {
    if (error instanceof AxiosError) {
      const errorData = errorResponse.safeParse(error.response?.data);
      if (errorData.success) {
        for (const [path, errors] of Object.entries(errorData.data.errors)) {
          const control = form.get(path);
          if (!control) {
            form.setErrors({ unknown: errors[0] });
            continue;
          }

          control.setErrors({ unknown: errors[0] });
          control.markAsDirty();
        }
      } else {
        console.error('Network error', error.response?.data);
      }
    } else {
      console.error('Unknown error', error);
    }

    this.snackbar.open(this.getErrorMessage(error), 'OK', {
      duration: 5000,
    });
  }

  private getErrorMessage(error: AxiosError | unknown): string {
    const fallback = 'Valami hiba történt mentés közben!';

    if (!(error instanceof AxiosError)) {
      return fallback;
    }

    const errorData = errorResponse.safeParse(error.response?.data);
    if (errorData.success) {
      const firstFieldError = Object.values(errorData.data.errors)[0]?.[0];

      return firstFieldError ?? errorData.data.message ?? fallback;
    }

    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }

    return fallback;
  }
}
